/**
 * Rapier-backed implementation of `CollisionWorld`.
 *
 * Rapier is used only for collider storage, broad phase and shape queries.
 * There are no dynamic bodies and no Rapier character controller: the player
 * motor (movement/PlayerMotor.ts) owns all motion rules. Rapier works in
 * 32-bit floats; see MOVEMENT_SPEC.md "Numerical precision".
 */
import type * as RAPIER from '@dimforge/rapier3d-compat';
import { loadRapier, rapier } from './rapier';
import { countResource } from '../debug/LiveResources';
import { quatFromUp, type QuatLike, type Vec3 } from '../core/Vec3';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';
import { ProximityContact, type CapsuleShape, type CollisionWorld, type QueryStats, type SweepHit } from './CollisionQueries';

let initPromise: Promise<void> | null = null;

/** Initialises the Rapier WASM module once. Safe to call repeatedly. */
export function initPhysics(): Promise<void> {
  initPromise ??= loadRapier().catch((err: unknown) => {
    initPromise = null;
    throw err;
  });
  return initPromise;
}

const now = (): number => globalThis.performance.now();

/**
 * Rapier reports normals in f32; on axis-aligned faces this leaves ~1e-6
 * components that would slowly bend velocities. Components below the f32
 * noise floor are zeroed before renormalising in f64.
 */
const NORMAL_NOISE = 2e-5;
const RAY_BACKOFF = 0.05;
/** cos(2°) */
const FACE_AGREEMENT = Math.cos((2 * Math.PI) / 180);
/**
 * Two sweep hits whose impact points are within this distance of each other
 * along the sweep count as simultaneous; the tie is broken by priority
 * (lethal first, then solid id), never by Rapier's enumeration order.
 */
const TOI_TIE_DISTANCE = 1e-5;

/** Canonical contact priority: lethal first, then by solid id. Negative = a first. */
function contactPriority(aLethal: boolean, aId: string, bLethal: boolean, bId: string): number {
  if (aLethal !== bLethal) return aLethal ? -1 : 1;
  return aId < bId ? -1 : aId > bId ? 1 : 0;
}

/** out = q ⊗ v ⊗ q⁻¹ (unit quaternion rotation). */
function rotateByQuat(v: Readonly<{ x: number; y: number; z: number }>, q: Readonly<QuatLike>, out: Vec3): Vec3 {
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  return out.set(v.x + q.w * tx + (q.y * tz - q.z * ty), v.y + q.w * ty + (q.z * tx - q.x * tz), v.z + q.w * tz + (q.x * ty - q.y * tx));
}

function cleanNormal(n: Vec3): Vec3 {
  if (Math.abs(n.x) < NORMAL_NOISE) n.x = 0;
  if (Math.abs(n.y) < NORMAL_NOISE) n.y = 0;
  if (Math.abs(n.z) < NORMAL_NOISE) n.z = 0;
  return n.normalize();
}

export class PhysicsWorld implements CollisionWorld {
  readonly world: RAPIER.World;
  private readonly materials = new Map<number, SurfaceMaterialDef>();
  private readonly solidIds = new Map<number, string>();
  readonly stats: QueryStats = { queries: 0, queryMs: 0 };
  private readonly rot: QuatLike = { x: 0, y: 0, z: 0, w: 1 };
  private readonly shapeCache = new Map<string, RAPIER.Capsule>();
  private finalized = false;
  private readonly ray = new (rapier().Ray)({ x: 0, y: 0, z: 0 }, { x: 0, y: -1, z: 0 });
  private readonly rayHit = new (rapier().RayIntersection)(0, { x: 0, y: 1, z: 0 });
  private readonly candidates: RAPIER.Collider[] = [];
  private readonly candidateToi: number[] = [];
  private readonly zero = { x: 0, y: 0, z: 0 };
  private readonly aabbCenter = { x: 0, y: 0, z: 0 };
  private readonly aabbHalf = { x: 0, y: 0, z: 0 };

  private disposed = false;

  constructor() {
    this.world = new (rapier().World)({ x: 0, y: 0, z: 0 });
    countResource('physicsWorlds', 1);
  }

  get colliderCount(): number {
    return this.materials.size;
  }

  addCollider(desc: RAPIER.ColliderDesc, material: SurfaceMaterialDef, solidId: string): number {
    if (this.finalized) throw new Error('PhysicsWorld: colliders must be added before finalize()');
    const c = this.world.createCollider(desc);
    this.materials.set(c.handle, material);
    this.solidIds.set(c.handle, solidId);
    return c.handle;
  }

  /** Builds acceleration structures. Rapier only answers queries after a step. */
  finalize(): void {
    this.world.step();
    this.finalized = true;
  }

  materialOf(colliderId: number): SurfaceMaterialDef | null {
    return this.materials.get(colliderId) ?? null;
  }

  solidIdOf(colliderId: number): string | null {
    return this.solidIds.get(colliderId) ?? null;
  }

  resetStats(): void {
    this.stats.queries = 0;
    this.stats.queryMs = 0;
  }

  private capsule(shape: CapsuleShape, inflate = 0): RAPIER.Capsule {
    const key = `${shape.halfSegment}:${shape.radius + inflate}`;
    let s = this.shapeCache.get(key);
    if (!s) {
      s = new (rapier().Capsule)(shape.halfSegment, shape.radius + inflate);
      this.shapeCache.set(key, s);
    }
    return s;
  }

  sweepCapsule(center: Readonly<Vec3>, up: Readonly<Vec3>, delta: Readonly<Vec3>, shape: CapsuleShape, targetDistance: number, out: SweepHit): boolean {
    const t0 = now();
    quatFromUp(up, this.rot);
    const capsule = this.capsule(shape);
    // Order-independent sweep: every candidate collider is cast individually
    // with the same full bound (Rapier's world-level cast shrinks its bound as
    // it traverses the BVH, so per-collider TOIs depended on traversal order).
    const ext = shape.radius + shape.halfSegment + targetDistance + 1e-3;
    this.aabbCenter.x = center.x + delta.x * 0.5;
    this.aabbCenter.y = center.y + delta.y * 0.5;
    this.aabbCenter.z = center.z + delta.z * 0.5;
    this.aabbHalf.x = Math.abs(delta.x) * 0.5 + ext;
    this.aabbHalf.y = Math.abs(delta.y) * 0.5 + ext;
    this.aabbHalf.z = Math.abs(delta.z) * 0.5 + ext;
    const cands = this.candidates;
    const tois = this.candidateToi;
    cands.length = 0;
    tois.length = 0;
    let best = Infinity;
    this.world.collidersWithAabbIntersectingAabb(this.aabbCenter, this.aabbHalf, (collider) => {
      if (collider.isSensor()) return true;
      const h = collider.castShape(this.zero, capsule, center, this.rot, delta, targetDistance, 1, false);
      if (h) {
        cands.push(collider);
        tois.push(h.time_of_impact);
        if (h.time_of_impact < best) best = h.time_of_impact;
      }
      return true;
    });
    let chosen: RAPIER.Collider | null = null;
    if (cands.length > 0) {
      const len = Math.hypot(delta.x, delta.y, delta.z);
      const tie = len > 0 ? TOI_TIE_DISTANCE / len : 0;
      for (let i = 0; i < cands.length; i++) {
        if (tois[i]! > best + tie) continue;
        const c = cands[i]!;
        if (!chosen || contactPriority(this.isLethal(c.handle), this.solidIds.get(c.handle) ?? '', this.isLethal(chosen.handle), this.solidIds.get(chosen.handle) ?? '') < 0) chosen = c;
      }
    }
    const hit = chosen ? chosen.castShape(this.zero, capsule, center, this.rot, delta, targetDistance, 1, false) : null;
    this.stats.queries++;
    this.stats.queryMs += now() - t0;
    if (!chosen || !hit) return false;
    out.fraction = Math.min(1, Math.max(0, hit.time_of_impact));
    // Collider.castShape reports normal1/witness1 in the collider's local frame.
    const q = chosen.rotation();
    rotateByQuat(hit.normal1, q, out.normal);
    rotateByQuat(hit.witness1, q, out.point).add(chosen.translation());
    this.refineFaceNormal(chosen, out.point, out.normal);
    cleanNormal(out.normal);
    out.colliderId = chosen.handle;
    out.material = this.materials.get(chosen.handle) ?? null;
    return true;
  }

  private isLethal(handle: number): boolean {
    return this.materials.get(handle)?.kind === 'lethal';
  }

  /** Insertion sort into canonical priority order (allocation-free; lists are short). */
  private sortContacts(out: ProximityContact[], count: number): void {
    for (let i = 1; i < count; i++) {
      const item = out[i]!;
      const lethal = item.material?.kind === 'lethal';
      const id = this.solidIds.get(item.colliderId) ?? '';
      let j = i - 1;
      while (j >= 0) {
        const o = out[j]!;
        if (contactPriority(o.material?.kind === 'lethal', this.solidIds.get(o.colliderId) ?? '', lethal, id) <= 0) break;
        out[j + 1] = o;
        j--;
      }
      out[j + 1] = item;
    }
  }

  /**
   * Swept-shape normals carry ~1e-3 rad of solver noise on flat faces. When a
   * short ray at the contact point reports a face normal that agrees with the
   * swept normal to within FACE_AGREEMENT, the exact face normal is used.
   * At edges and vertices the two disagree and the swept normal is kept.
   * Returns whether the contact lies on a face.
   */
  private refineFaceNormal(collider: RAPIER.Collider, point: RAPIER.Vector, normal: Vec3): boolean {
    const o = this.ray.origin;
    o.x = point.x + normal.x * RAY_BACKOFF;
    o.y = point.y + normal.y * RAY_BACKOFF;
    o.z = point.z + normal.z * RAY_BACKOFF;
    const d = this.ray.dir;
    d.x = -normal.x;
    d.y = -normal.y;
    d.z = -normal.z;
    const hit = collider.castRayAndGetNormal(this.ray, RAY_BACKOFF * 2, true, this.rayHit);
    if (!hit) return false;
    const n = hit.normal;
    if (n.x * normal.x + n.y * normal.y + n.z * normal.z <= FACE_AGREEMENT) return false;
    normal.copy(n);
    return true;
  }

  contacts(center: Readonly<Vec3>, up: Readonly<Vec3>, shape: CapsuleShape, maxDistance: number, out: ProximityContact[]): number {
    const t0 = now();
    quatFromUp(up, this.rot);
    const probe = this.capsule(shape);
    const inflated = this.capsule(shape, maxDistance);
    let count = 0;
    this.world.intersectionsWithShape(
      center,
      this.rot,
      inflated,
      (collider) => {
        const c = collider.contactShape(probe, center, this.rot, maxDistance);
        if (c && c.distance <= maxDistance) {
          let entry = out[count];
          if (!entry) {
            entry = new ProximityContact();
            out[count] = entry;
          }
          entry.distance = c.distance;
          entry.normal.copy(c.normal1);
          entry.onFace = this.refineFaceNormal(collider, c.point1, entry.normal);
          cleanNormal(entry.normal);
          entry.point.copy(c.point1);
          entry.colliderId = collider.handle;
          entry.material = this.materials.get(collider.handle) ?? null;
          count++;
        }
        return true;
      },
      rapier().QueryFilterFlags.EXCLUDE_SENSORS,
    );
    this.sortContacts(out, count);
    this.stats.queries++;
    this.stats.queryMs += now() - t0;
    return count;
  }

  /** Line segments for the debug collider view (positions, RGBA colours). */
  debugLines(): { vertices: Float32Array; colors: Float32Array } {
    const b = this.world.debugRender();
    return { vertices: b.vertices, colors: b.colors };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    countResource('physicsWorlds', -1);
    this.world.free();
    this.materials.clear();
    this.shapeCache.clear();
  }
}
